import { ActionCreator, Action, Dispatch } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { IApplicationState } from '../store'
import { VestigingEnum } from 'shared/models/Enum'
import { IOrderType } from 'shared/models/IOrderType'
import GlnService from 'shared/service/GlnService'
import { IResponseAction } from '../interfaces'
import { IGetOverflowTimeslotsFilters } from 'shared/models/IGetOverflowTimeslotsFilters'
import { IOverflowTimeslotModel } from 'shared/models/IOverflowTimeslotModel'
import { VasModel } from 'shared/models/Vas/VasModel'
import VasService from 'shared/service/VasService'

export enum Actions {
  SetVestigingAction = 'SetVestigingAction',
  SetPossibleOrderTypesRequestAction = 'SetPossibleOrderTypesRequestAction',
  SetPossibleOrderTypesResponseAction = 'SetPossibleOrderTypesResponseAction',
  GetOverflowTimeslotsRequestAction = 'GetOverflowTimeslotsRequestAction',
  GetOverflowTimeslotsResponseAction = 'GetOverflowTimeslotsResponseAction',
  GetAllVasCodesForWarehouseRequestAction = 'GetAllVasCodesForWarehouseRequestAction',
  GetAllVasCodesForWarehouseResponseAction = 'GetAllVasCodesForWarehouseResponseAction',
}

export interface ISetVestigingResponseAction
  extends Action<Actions.SetVestigingAction> {
  payload: VestigingEnum
}
export interface ISetPossibleOrderTypesResponseAction
  extends IResponseAction<Actions.SetPossibleOrderTypesResponseAction> {
  payload: IOrderType[]
}
export interface ISetPossibleOrderTypesRequestAction
  extends Action<Actions.SetPossibleOrderTypesRequestAction> {}

export interface IGetOverflowTimeslotsRequestAction
  extends Action<Actions.GetOverflowTimeslotsRequestAction> {
  payload: IGetOverflowTimeslotsFilters
}
export interface IGetOverflowTimeslotsResponseAction
  extends IResponseAction<Actions.GetOverflowTimeslotsResponseAction> {
  payload: IOverflowTimeslotModel[]
}

export interface IGetAllVasCodesForWarehouseRequestAction
  extends Action<Actions.GetAllVasCodesForWarehouseRequestAction> {
  payload: { warehouseId: number }
}

export interface IGetAllVasCodesForWarehouseResponseAction
  extends IResponseAction<Actions.GetAllVasCodesForWarehouseResponseAction> {
  payload: VasModel[]
}

export type GlobalActions =
  | ISetVestigingResponseAction
  | ISetPossibleOrderTypesResponseAction
  | ISetPossibleOrderTypesRequestAction
  | IGetOverflowTimeslotsRequestAction
  | IGetOverflowTimeslotsResponseAction
  | IGetAllVasCodesForWarehouseRequestAction
  | IGetAllVasCodesForWarehouseResponseAction

export const getPossibleOrderTypesActionCreator: ActionCreator<
  ThunkAction<
    Promise<ISetPossibleOrderTypesResponseAction>,
    IApplicationState,
    null,
    ISetPossibleOrderTypesResponseAction
  >
> = () => {
  return async (dispatch: Dispatch, getState: () => IApplicationState) => {
    const state = getState()
    const existingOrderTypes = state.globalState.possibleGroupLinkOrderTypes

    if (existingOrderTypes && existingOrderTypes.length > 0) {
      const cachedResponse: ISetPossibleOrderTypesResponseAction = {
        success: true,
        errorMessage: null,
        payload: existingOrderTypes,
        type: Actions.SetPossibleOrderTypesResponseAction,
      }

      dispatch(cachedResponse)
      return Promise.resolve(cachedResponse)
    }

    const requestAction: ISetPossibleOrderTypesRequestAction = {
      type: Actions.SetPossibleOrderTypesRequestAction,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.getPossibleOrderTypes()
      const response: ISetPossibleOrderTypesResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.SetPossibleOrderTypesResponseAction,
      }

      if (
        (!result.success && result.errorCode === 404) ||
        (result.success && result.payLoad.length === 0)
      ) {
        response.success = false
        response.errorMessage = 'Geen ordertypes gevonden'
      }

      return dispatch(response)
    } catch (e) {
      const errorReponse: ISetPossibleOrderTypesResponseAction = {
        success: false,
        errorMessage: `fout bij ophalen ordertypes: ${e.message}`,
        payload: null,
        type: Actions.SetPossibleOrderTypesResponseAction,
      }

      return dispatch(errorReponse)
    }
  }
}

export const getOverflowTimeslotsActionCreator: ActionCreator<
  ThunkAction<
    Promise<IGetOverflowTimeslotsResponseAction>,
    IApplicationState,
    null,
    IGetOverflowTimeslotsResponseAction
  >
> = (filters: IGetOverflowTimeslotsFilters) => {
  return async (dispatch: Dispatch) => {
    const requestAction: IGetOverflowTimeslotsRequestAction = {
      type: Actions.GetOverflowTimeslotsRequestAction,
      payload: filters,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.getOverflowTimeslots(filters)
      const response: IGetOverflowTimeslotsResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.GetOverflowTimeslotsResponseAction,
      }

      return dispatch(response)
    } catch (e) {
      const errorResponse: IGetOverflowTimeslotsResponseAction = {
        success: false,
        errorMessage: `Error fetching overflow timeslots: ${e.message}`,
        payload: null,
        type: Actions.GetOverflowTimeslotsResponseAction,
      }
      return dispatch(errorResponse)
    }
  }
}

export const getAllVasCodesForWarehouseActionCreator: ActionCreator<
  ThunkAction<
    Promise<IGetAllVasCodesForWarehouseResponseAction>,
    IApplicationState,
    null,
    IGetAllVasCodesForWarehouseResponseAction
  >
> = (warehouseId: number) => {
  return async (dispatch: Dispatch) => {
    const requestAction: IGetAllVasCodesForWarehouseRequestAction = {
      type: Actions.GetAllVasCodesForWarehouseRequestAction,
      payload: { warehouseId },
    }
    dispatch(requestAction)
    try {
      const result = await VasService.getAllVasCodesForWarehouse(warehouseId)
      const response: IGetAllVasCodesForWarehouseResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.GetAllVasCodesForWarehouseResponseAction,
      }

      return dispatch(response)
    } catch (e) {
      const errorResponse: IGetAllVasCodesForWarehouseResponseAction = {
        success: false,
        errorMessage: `Error fetching all VAS codes for warehouse: ${e.message}`,
        payload: null,
        type: Actions.GetAllVasCodesForWarehouseResponseAction,
      }
      return dispatch(errorResponse)
    }
  }
}
